import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {RejectReasonCodeValidator} from '@application/validators/reject-reason-code-validator';
import {IdName} from '@domain/id-name';
import {MachineType} from '@domain/machine/machine-type.enum';
import {Permission} from '@domain/profile/permission.enum';
import {RejectReason} from '@domain/settings/reject-reason/reject-reason';
import {RejectType} from '@domain/settings/reject-reason/reject-type.enum';
import {HttpMachineTypesService} from '@infrastructure/http/machine-type/http-machine-types.service';
import {HttpRejectReasonGroupsService} from '@infrastructure/http/reject-reason-group/http-reject-reason-groups.service';
import {HttpRejectReasonsService} from '@infrastructure/http/reject-reason/http-reject-reasons.service';
import {TextileService} from '@presentation/pages/textile-data/textile-data-overview/textile.service';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  ColDefBuilderFactoryService,
  Color,
  ContentSwitcherNavigationInput,
  convertHexadecimalColorCodeToRGB,
  CrudObjectSelectionDialogComponent,
  CrudObjectSelectionDialogInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  SaveType,
  StringUtils,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {Observable, takeUntil} from 'rxjs';

@Component({
  templateUrl: './add-reject-reason-page.component.html'
})
export class AddRejectReasonPageComponent extends BaseComponent implements OnInit {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;

  public addRejectReasonForm: FormGroup<{
    name: FormControl<string>;
    color: FormControl<string>;
    rejectType: FormControl<RejectType>;
    code: FormControl<number>;
    displayOrder: FormControl<number>;
    machineTypes: FormControl<IdName[]>;
    group: FormControl<IdName>;
    description: FormControl<string>;
  }>;

  public rejectReasonToSave: RejectReason;
  public selectedMenuItem: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};
  public readonly MAX_CODE_VALUE = 255;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_REJECT_REASON;
  public readonly SAVE_TYPE = SaveType;
  public readonly REJECT_TYPE = RejectType;
  public readonly REJECT_REASON_GROUP_PERMISSION = Permission.REJECT_REASON_SELECT_GROUP_PERMISSION;
  public refreshObjectSelection: boolean = false;

  private machineTypesConfig: CrudOverviewDataConfig<IdName>;
  private groupConfig: CrudOverviewDataConfig<IdName>;

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly activatedRoute: ActivatedRoute,
    private readonly colDefBuilderFactory: ColDefBuilderFactoryService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly overviewDataBuilderFactory: CrudOverviewDataBuilderFactoryService<IdName>,
    private readonly formBuilder: FormBuilder,
    private readonly machineTypes: HttpMachineTypesService,
    private readonly rejectReasons: HttpRejectReasonsService,
    private readonly rejectReasonGroups: HttpRejectReasonGroupsService,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService,
    private readonly removeConfirmation: RemoveConfirmationService,
    public readonly navigationHelper: NavigationHelperPrototypeService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setFormFields();
    this.setBuilderConfigForMachineTypes();
    this.setBuilderConfigForGroup();

    const id = this.activatedRoute.snapshot.params.id ?? this.itemId;
    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.rejectReasons
        .getById(parseInt(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((rejectReason: RejectReason) => {
          this.itemId = rejectReason.id;
          this.rejectReasonToSave = rejectReason;
          this.setFormValues();
        });
    }

    this.addRejectReasonForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((_: any) => {
      this.rejectReasonToSave = this.getCurrentRejectReason();
    });
  }

  public selectMachineTypes(): void {
    this.refreshObjectSelection = false;
    const machineTypesControl = this.addRejectReasonForm.controls.machineTypes;
    this.machineTypesConfig.selectedObjects = Array.isArray(machineTypesControl.value) ? machineTypesControl.value : [];

    this.navigationHelper
      .navigateWithContentSwitcher<IdName[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.machineTypesConfig
          } as CrudObjectSelectionDialogInput
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machineTypes: IdName[]) => {
        if (!AssertionUtils.isEmpty(machineTypes)) {
          machineTypesControl.setValue(machineTypes);
        }
      });
  }

  public selectGroup(): void {
    const groupControl = this.addRejectReasonForm.controls.group;
    this.groupConfig.selectedObjects = [groupControl.value];

    this.navigationHelper
      .navigateWithContentSwitcher<IdName[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.groupConfig
          } as CrudObjectSelectionDialogInput
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((groups: IdName[]) => {
        if (!AssertionUtils.isEmpty(groups)) {
          groupControl.setValue(groups[0]);
        }
      });
  }

  public saveRejectReason(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addRejectReasonForm], this.document);
    if (isValid) {
      this.saving = true;

      this.rejectReasonToSave = this.getCurrentRejectReason();
      const request: Observable<void | number> = this.isEditingRejectReason() ? this.rejectReasons.update(this.rejectReasonToSave) : this.rejectReasons.save(this.rejectReasonToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe({
        next: (id: number) => {
          if (this.navigationHelper.contentSwitcherDialogActive()) {
            if (saveType === SaveType.SAVE_AND_CLOSE) {
              this.rejectReasonToSave.id = id;

              this.navigationHelper.navigateBack(this.isEditingRejectReason() ? [] : this.rejectReasonToSave);
            } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
              this.setFormFields();
              this.refreshObjectSelection = true;
            }
            return;
          }

          this.textileService.navigateAndShowToast(saveType, TextileDataType.REJECT_REASON, 'BMSCONFIG.REJECT_REASON.REJECT_REASON', this.isEditingRejectReason(), this.rejectReasonToSave.name, id);
        },
        complete: () => {
          this.finalizeSaving();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingRejectReason() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public duplicateRejectReason(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateWithContentSwitcher({
        componentInfo: {
          component: AddRejectReasonPageComponent,
          componentInputs: {pageType: PageTypeEnum.DUPLICATE, itemId: this.itemId, canShowHeader: true}
        },
        forgetCurrentEntry: true
      });
    } else {
      this.navigationHelper.navigateWithRoute({
        route: RouteUtils.paths.settings.rejectReason.duplicateRejectReason.absolutePath.replace(':id', this.itemId.toString()),
        forgetCurrentEntry: true
      });
    }
  }

  public deleteRejectReason(): void {
    this.removeConfirmation.open(this.rejectReasonToSave, this.rejectReasons.delete(this.itemId), 'BMSCONFIG.REJECT_REASON.REJECT_REASON', null, null, true);
  }

  public isEditingRejectReason(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.EDIT;
    }

    return this.activatedRoute.snapshot.routeConfig.path === RouteUtils.paths.settings.rejectReason.editRejectReason.path;
  }

  public navigateBack(): void {
    this.navigationHelper.navigateBack(this.rejectReasonToSave);
  }

  public getLoadLink(controlName: string = 'name'): string {
    return ErrorHandlers.getLoadLink(RouteUtils.paths.settings.rejectReason.editRejectReason.absolutePath, this.addRejectReasonForm.controls[controlName]);
  }

  public resetCodeAsyncValidator(): void {
    this.addRejectReasonForm.controls.code.setAsyncValidators(null);
    this.setCodeAsyncValidator();
    this.addRejectReasonForm.controls.code.updateValueAndValidity();
  }

  public getLoadEntry(): ContentSwitcherNavigationInput {
    return {
      componentInfo: {
        component: AddRejectReasonPageComponent,
        componentInputs: {pageType: PageTypeEnum.EDIT, itemId: this.addRejectReasonForm?.controls?.name?.errors?.entityId, canShowHeader: true}
      }
    };
  }

  private setFormFields(): void {
    this.addRejectReasonForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required),
      color: this.formBuilder.control('#000000'),
      rejectType: this.formBuilder.control(RejectType.REJECT),
      code: this.formBuilder.control(null, [Validators.required, Validators.min(1), Validators.max(this.MAX_CODE_VALUE)]),
      displayOrder: this.formBuilder.control(null),
      machineTypes: this.formBuilder.control(null, Validators.required),
      group: this.formBuilder.control(null),
      description: this.formBuilder.control(null)
    });

    this.setNameAsyncValidator();
    this.setCodeAsyncValidator();

    this.selectedMenuItem.form = this.addRejectReasonForm;
  }

  private setFormValues(): void {
    const name = this.isEditingRejectReason() ? this.rejectReasonToSave.name : null;

    this.addRejectReasonForm.reset({
      name: name,
      color: this.rejectReasonToSave.color.hexadecimalColorCode,
      rejectType: this.rejectReasonToSave.rejectType,
      code: this.rejectReasonToSave.code,
      displayOrder: this.rejectReasonToSave.displayOrder,
      machineTypes: this.rejectReasonToSave.machineTypes,
      group: this.rejectReasonToSave.group,
      description: this.rejectReasonToSave.description
    });

    this.setNameAsyncValidator(name);
    this.setCodeAsyncValidator();
  }

  private setNameAsyncValidator(name: string = null): void {
    this.addRejectReasonForm.controls.name.addAsyncValidators([AsyncUniqueValidator.createValidator(this.rejectReasons, name)]);
  }

  private setCodeAsyncValidator(): void {
    const machineTypeIds = this.addRejectReasonForm.value.machineTypes?.map((machineType: IdName) => machineType.id);
    this.addRejectReasonForm.controls.code.addAsyncValidators([RejectReasonCodeValidator.createRejectReasonCodeValidator(this.rejectReasons, machineTypeIds, this.rejectReasonToSave?.code)]);

    this.addRejectReasonForm.controls.machineTypes.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.resetCodeAsyncValidator());
  }

  private getCurrentRejectReason(): RejectReason {
    return new RejectReason(
      this.isEditingRejectReason() ? this.itemId : null,
      this.addRejectReasonForm.value.name,
      new Color(null, null, convertHexadecimalColorCodeToRGB(this.addRejectReasonForm.controls.color.value), false),
      this.addRejectReasonForm.value.rejectType,
      this.addRejectReasonForm.value.code,
      this.addRejectReasonForm.value.displayOrder,
      this.addRejectReasonForm.value.machineTypes,
      this.addRejectReasonForm.value.group,
      this.addRejectReasonForm.value.description
    );
  }

  private setBuilderConfigForMachineTypes(): void {
    const colDefs = [
      this.colDefBuilderFactory.getBuilder().withField('name', true).withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactory.getBuilder().withField('manufacturer.name', true).withHeaderName('MACHINE.MANUFACTURER').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactory.getBuilder().withField('technology.name', true).withHeaderName('MACHINE.TECHNOLOGY').withComparator(StringUtils.stringComparator).build()
    ];

    this.machineTypesConfig = this.overviewDataBuilderFactory
      .getBuilder()
      .withEntityName('BMSCONFIG.REJECT_REASON.PLASTIC_MACHINE_TYPES')
      .withGridOptions(colDefs, GridIdentifier.SELECT_PLASTICS_MACHINE_TYPE, null, (builder: GridOptionsBuilder) => builder.withRowSelection(true).build())
      .withRowData(this.machineTypes.getAll(MachineType.PLASTIC_MACHINE))
      .build();
  }

  private setBuilderConfigForGroup(): void {
    const colDefs = [this.colDefBuilderFactory.getBuilder().withField('name', true).withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).build()];

    this.groupConfig = this.overviewDataBuilderFactory
      .getBuilder()
      .withEntityName('BMSCONFIG.REJECT_REASON.REJECT_REASON_GROUP')
      .withGridOptions(colDefs, GridIdentifier.SELECT_REJECT_REASON_GROUP, null, (builder: GridOptionsBuilder) => builder.withRowSelection(false, false).build())
      .withRowData(this.rejectReasonGroups.getAll())
      .build();
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant('BMSCONFIG.REJECT_REASON.REJECT_REASON', {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }
}
